import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { pointerJour } from '@/services/etatsLocation';
import { type GasoilConfig, type PointageSortie, type PointageValeurs, PointageForm, PopoverPointage } from './PointageForm';
import { getEntreprise, jourReposJs } from '@/services/parametres';
import { toast } from '@/store/toastStore';

/* Mêmes conventions de dates que le module Pointage employés. */
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
/** Samedi qui ouvre la semaine contenant `d` (semaine du samedi au vendredi). */
const samediSemaine = (d: Date) => addDays(d, -(((d.getDay() - 6) + 7) % 7));
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const EN_TETES = ['Sam', 'Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
const JOUR_NOM = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

/** Pré-remplit le formulaire avec ce qui est déjà pointé ce jour-là, pour pouvoir le corriger. */
function valeursDepuis(j?: JourPointe): Partial<PointageValeurs> {
  if (!j) return {};
  const litres = j.gasoil_litres ? String(j.gasoil_litres) : '';
  const montant = j.gasoil_montant ? String(j.gasoil_montant) : '';
  if (j.heure_debut && j.heure_fin) {
    return { presence: 'HORAIRES', debut: j.heure_debut.slice(0, 5), fin: j.heure_fin.slice(0, 5),
      motif: (j.motif_perte as PointageValeurs['motif']) ?? '', litres, montant };
  }
  return {
    presence: j.journees === 0 ? 'AUCUNE' : j.journees === 0.5 ? 'DEMI' : 'PLEINE',
    heuresSup: j.heures_sup ? String(j.heures_sup) : '', litres, montant,
  };
}

/** Ce qui est pointé un jour donné (fourni par le décompte). */
export interface JourPointe {
  journees: number;
  heures_sup: number;
  /** Gasoil du jour (location entrante) et horaires précis éventuels, pour pré-remplir le formulaire. */
  gasoil_litres?: number;
  gasoil_montant?: number;
  heure_debut?: string | null;
  heure_fin?: string | null;
  motif_perte?: string | null;
}

interface Props {
  documentId: string;
  sens: 'SORTANTE' | 'ENTRANTE';
  /** ISO date → pointage du jour (issu des lignes du décompte). */
  jours: Record<string, JourPointe>;
  /** Mois affiché par défaut (ex. début de la location). */
  moisInitial?: string | null;
  modifiable: boolean;
  onChange: () => void;
  /** Gasoil à suivre (location entrante, hors gasoil du loueur) ; null/absent = pas de saisie de gasoil. */
  gasoil?: GasoilConfig | null;
}

/**
 * Calendrier de pointage d'UNE location : un mois de cases vides, on clique un jour
 * pour déclarer que la machine a travaillé (journée pleine, demi-journée, heures sup),
 * ou pour effacer. Même geste que le pointage des employés — la date vient de la case
 * cliquée, jamais d'un champ à ressaisir.
 */
export function CalendrierLocation({ documentId, sens, jours, moisInitial, modifiable, onChange, gasoil }: Props) {
  const [cursor, setCursor] = useState(() => (moisInitial ? new Date(`${moisInitial}T00:00:00`) : new Date()));
  const [menu, setMenu] = useState<{ date: string; x: number; y: number } | null>(null);
  // Jour de repos paramétré (Paramètres → Entreprise) : il grise la case, il ne l'interdit pas.
  const { data: entreprise } = useQuery({ queryKey: ['entreprise'], queryFn: getEntreprise });
  const jourReposIdx = jourReposJs(entreprise?.jour_repos_hebdo);
  const estRepos = (d: Date) => d.getDay() === jourReposIdx;

  const pointerM = useMutation({
    mutationFn: (v: { date: string; sortie: PointageSortie }) =>
      pointerJour(sens, documentId, {
        date: v.date, journees: v.sortie.journees, heures_sup: v.sortie.heures_sup,
        heure_debut: v.sortie.heure_debut, heure_fin: v.sortie.heure_fin, motif_perte: v.sortie.motif_perte,
        gasoil_litres: v.sortie.gasoil_litres, gasoil_montant: v.sortie.gasoil_avance,
      }),
    onSuccess: () => { onChange(); setMenu(null); },
    onError: (e) => { toast.error(getApiErrorMessage(e)); setMenu(null); },
  });

  // Semaines du mois affiché (samedi → vendredi), complétées avant/après pour un vrai calendrier.
  const semaines = useMemo<Date[][]>(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const premier = new Date(y, m, 1);
    const dernier = new Date(y, m + 1, 0);
    const out: Date[][] = [];
    let debut = samediSemaine(premier);
    while (debut <= dernier) {
      out.push(Array.from({ length: 7 }, (_, i) => addDays(debut, i)));
      debut = addDays(debut, 7);
    }
    return out;
  }, [cursor]);

  const navMois = (d: number) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + d, 1));
  const totalMois = Object.entries(jours)
    .filter(([k]) => k.startsWith(`${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`))
    .reduce((acc, [, v]) => ({ j: acc.j + v.journees, h: acc.h + v.heures_sup }), { j: 0, h: 0 });

  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navMois(-1)} aria-label="Mois précédent"><ChevronLeft className="size-4" /></Button>
          <span className="min-w-[140px] text-center text-sm font-semibold text-content-primary">{MOIS[cursor.getMonth()]} {cursor.getFullYear()}</span>
          <Button variant="secondary" size="sm" onClick={() => navMois(1)} aria-label="Mois suivant"><ChevronRight className="size-4" /></Button>
          <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>Ce mois-ci</Button>
        </div>
        <span className="text-xs text-content-secondary">
          {Math.round(totalMois.j * 10) / 10} jour(s){totalMois.h ? ` · ${Math.round(totalMois.h * 10) / 10} h sup` : ''} ce mois-ci
        </span>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {EN_TETES.map((t) => (
          <div key={t} className="pb-1 text-center text-[11px] font-semibold uppercase text-content-muted">{t}</div>
        ))}
        {semaines.flat().map((d) => {
          const cle = iso(d);
          const p = jours[cle];
          const horsMois = d.getMonth() !== cursor.getMonth();
          const pointe = !!p && (p.journees > 0 || p.heures_sup > 0);
          return (
            <button
              key={cle}
              type="button"
              disabled={!modifiable}
              onClick={(ev) => setMenu({ date: cle, x: ev.clientX, y: ev.clientY })}
              title={modifiable
                ? `Pointer le ${d.getDate()}/${pad(d.getMonth() + 1)}${estRepos(d) ? ' (jour de repos — pointage possible)' : ''}`
                : 'Décompte validé — lecture seule'}
              className={cn(
                'flex h-16 flex-col items-center justify-center rounded-md border text-sm transition-colors',
                horsMois && 'opacity-40',
                estRepos(d) && !pointe && 'bg-status-stuck/5',
                estRepos(d) && pointe && 'ring-1 ring-status-stuck/60',   // travaillé un jour de repos
                pointe
                  ? (sens === 'SORTANTE' ? 'border-status-done bg-status-done/15' : 'border-brand-primary bg-brand-primary/15')
                  : 'border-border bg-surface-primary/40',
                modifiable ? 'cursor-pointer hover:border-brand-primary hover:bg-brand-primary/10' : 'cursor-not-allowed',
              )}
            >
              <span className={cn('text-[11px]', pointe ? 'text-content-secondary' : 'text-content-muted')}>{d.getDate()}</span>
              {pointe ? (
                <>
                  <span className="font-bold tabular-nums text-content-primary">{p.journees === 0.5 ? '½' : (p.journees || '')}</span>
                  {p.heures_sup > 0 && <span className="text-[10px] text-content-secondary">+{p.heures_sup}h</span>}
                </>
              ) : (
                <span className="text-content-muted/40">—</span>
              )}
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] text-content-muted">
        Cliquez un jour pour déclarer que la machine a travaillé (présence, heures supplémentaires{sens === 'ENTRANTE' ? ', gasoil' : ''})
        ou pour l'effacer. Semaine du samedi au vendredi · repos le {JOUR_NOM[jourReposIdx].toLowerCase()}
        (modifiable dans Paramètres). Un jour de repos reste pointable : une machine qui travaille se déclare.
      </p>

      {menu && modifiable && (
        <PopoverPointage x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <PointageForm
            key={menu.date}
            titre={`${menu.date.slice(8, 10)}/${menu.date.slice(5, 7)}/${menu.date.slice(0, 4)}`}
            enRepos={estRepos(new Date(`${menu.date}T00:00:00`))}
            horaires={sens === 'ENTRANTE'}
            gasoil={sens === 'ENTRANTE' ? (gasoil ?? null) : null}
            initial={valeursDepuis(jours[menu.date])}
            jourAvecGasoil={(jours[menu.date]?.gasoil_litres ?? 0) > 0}
            enCours={pointerM.isPending}
            onEnregistrer={(sortie) => pointerM.mutate({ date: menu.date, sortie })}
            onEffacer={() => pointerM.mutate({
              date: menu.date,
              sortie: {
                journees: '0', heures_sup: '0',
                ...(gasoil && (jours[menu.date]?.gasoil_litres ?? 0) > 0 ? { gasoil_litres: '0', gasoil_avance: '0' } : {}),
              },
            })}
          />
        </PopoverPointage>
      )}
    </div>
  );
}
